import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import BookingModal from './BookingModal';
import Seo from './Seo';
import { Button } from './ui/button';
import { MapPin, Phone, Clock, Star, CheckCircle2, ArrowRight, Building2, Award, Users } from 'lucide-react';

const PHOTOS = {
  hero: 'https://customer-assets-jt897jd0.emergentagent.net/job_prachi-premium/artifacts/tqofaxeh_IMG_9848.JPG.webp',
  a: 'https://customer-assets-jt897jd0.emergentagent.net/job_prachi-premium/artifacts/918k476o_IMG_9890.JPG.webp',
  b: 'https://customer-assets-jt897jd0.emergentagent.net/job_prachi-premium/artifacts/crhf8376_IMG_9885.JPG.webp',
  c: 'https://customer-assets-jt897jd0.emergentagent.net/job_prachi-premium/artifacts/3v768xiy_IMG_9902.JPG.webp',
  d: 'https://customer-assets-jt897jd0.emergentagent.net/job_prachi-premium/artifacts/vrctfwoh_IMG_9958.JPG.webp',
};

const LocationLanding = ({ loc }) => {
  const [bookOpen, setBookOpen] = useState(false);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `https://prachi-premium.emergent.host/${loc.slug}`,
    name: `NutrifyMe by Nutritionist Prachi — Dietitian in ${loc.area}`,
    image: 'https://customer-assets-jt897jd0.emergentagent.net/job_prachi-premium/artifacts/92rpbx3r_nutrify_me_logo.png',
    description: loc.metaDescription,
    telephone: '+91-95454-05936',
    priceRange: '₹₹',
    address: {
      '@type': 'PostalAddress',
      streetAddress: `${loc.address.line1}, ${loc.address.line2}`,
      addressLocality: loc.address.area,
      addressRegion: 'Maharashtra',
      postalCode: '411014',
      addressCountry: 'IN',
    },
    geo: { '@type': 'GeoCoordinates', latitude: loc.address.geo.lat, longitude: loc.address.geo.lng },
    areaServed: loc.neighbourhoods.map((n) => ({ '@type': 'Place', name: `${n.name}, ${loc.city}` })),
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      opens: '09:00',
      closes: '19:00',
    },
    aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.9', reviewCount: '1200' },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: loc.faq.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-[#faf7f1]">
      <Seo
        title={loc.metaTitle.replace(' — Nutritionist Prachi', '')}
        description={loc.metaDescription}
        keywords={loc.keywords}
        path={`/${loc.slug}`}
        jsonLd={jsonLd}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      <Navbar onBook={() => setBookOpen(true)} />

      {/* Hero */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[#e8f2e2] opacity-60 blur-3xl" />
        <div className="absolute top-1/2 -left-32 w-[420px] h-[420px] rounded-full bg-[#fce7d5] opacity-50 blur-3xl" />
        <div className="max-w-6xl mx-auto px-6 relative grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/70 backdrop-blur border border-[#e5e0d3] mb-6">
              <MapPin className="w-3.5 h-3.5 text-[#3a5a2c]" />
              <span className="text-xs text-[#3a4a2c] tracking-wide">{loc.tagline}</span>
            </div>
            <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] text-[#1a2b17] tracking-tight">
              {loc.heroTitle}
            </h1>
            <p className="mt-6 text-lg text-[#4a5544] leading-relaxed max-w-2xl">{loc.heroSubtitle}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button onClick={() => setBookOpen(true)} className="h-13 px-8 rounded-full bg-[#3a5a2c] hover:bg-[#2d4622] text-white py-4">
                Book Free 15-min Call <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <a href="tel:+919545405936" className="h-13 px-8 rounded-full border border-[#1a2b17]/20 text-[#1a2b17] hover:bg-[#1a2b17] hover:text-white inline-flex items-center gap-2 transition">
                <Phone className="w-4 h-4" /> +91 95454 05936
              </a>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
              {[
                { icon: Users, val: '10,000+', label: 'Clients transformed' },
                { icon: Award, val: '4.9 ★', label: '1,200+ reviews' },
                { icon: Building2, val: loc.area, label: loc.since },
              ].map((s, i) => (
                <div key={i} className="p-4 rounded-2xl bg-white">
                  <s.icon className="w-4 h-4 text-[#3a5a2c]" />
                  <div className="font-serif text-xl text-[#1a2b17] mt-1">{s.val}</div>
                  <div className="text-[10px] tracking-widest uppercase text-[#7a8471] mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img src={PHOTOS.hero} alt={`Nutritionist Prachi — ${loc.area}`} className="w-full h-[520px] object-cover object-top" />
            </div>
          </div>
        </div>
      </section>

      {/* Neighborhoods */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-sm tracking-[0.2em] uppercase text-[#c9743a] mb-3">Areas we serve</div>
            <h2 className="font-serif text-4xl text-[#1a2b17]">Trusted dietitian for {loc.area} &amp; nearby</h2>
            <p className="mt-4 text-[#4a5544]">Whether you are in {loc.area} or the neighbouring hubs, our online + clinic care gives you the same personalised nutrition experience.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {loc.neighbourhoods.map((n) => (
              <div key={n.name} className="p-6 rounded-3xl bg-[#faf7f1] hover:bg-[#3a5a2c] hover:text-white transition-all duration-500 group cursor-default">
                <MapPin className="w-4 h-4 text-[#c9743a] group-hover:text-[#f4d5a8]" />
                <h3 className="font-serif text-xl mt-3">{n.name}</h3>
                <p className="text-sm mt-2 leading-relaxed text-[#4a5544] group-hover:text-white/80">{n.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section className="py-20 bg-[#faf7f1]">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-sm tracking-[0.2em] uppercase text-[#c9743a] mb-3">Why {loc.area} trusts us</div>
            <h2 className="font-serif text-4xl text-[#1a2b17] leading-tight">The most personalised nutrition clinic for {loc.area}</h2>
            <ul className="mt-8 space-y-4">
              {[
                'Founded and led by Nutritionist Prachi — 12+ years of clinical practice.',
                'Every plan is culturally aligned with Indian pantries and local lifestyles.',
                'In-clinic visits at Panchshil Soho Kharadi or online consults, whichever fits your day.',
                'Weekly check-ins, meal plan updates and WhatsApp support with your dietitian.',
                'Evidence-based, never influencer-driven. No crash diets, no meal replacements.',
                'Programs for PCOS, diabetes, thyroid, weight loss, pregnancy and sports nutrition.',
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-[#1a2b17]">
                  <CheckCircle2 className="w-5 h-5 text-[#3a5a2c] flex-shrink-0 mt-0.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <img src={PHOTOS.a} alt="Nutritionist Prachi" className="rounded-3xl h-80 object-cover w-full" />
            <img src={PHOTOS.b} alt="Consultation portrait" className="rounded-3xl h-80 object-cover w-full mt-8" />
            <img src={PHOTOS.c} alt="Dietitian Prachi" className="rounded-3xl h-64 object-cover w-full" />
            <img src={PHOTOS.d} alt="Prachi holding orange" className="rounded-3xl h-64 object-cover w-full mt-8" />
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-sm tracking-[0.2em] uppercase text-[#c9743a] mb-3">Reviews from {loc.area} &amp; nearby</div>
            <h2 className="font-serif text-4xl text-[#1a2b17]">Loved by {loc.area}</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {loc.reviews.map((r, i) => (
              <div key={i} className="p-8 rounded-3xl bg-[#faf7f1]">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(r.rating)].map((_, k) => <Star key={k} className="w-4 h-4 fill-[#e8a94b] text-[#e8a94b]" />)}
                </div>
                <p className="text-[#1a2b17] font-serif text-lg leading-relaxed italic">&ldquo;{r.text}&rdquo;</p>
                <div className="mt-5 flex items-center gap-2 text-sm">
                  <span className="font-medium text-[#1a2b17]">{r.name}</span>
                  <span className="text-[#7a8471]">· {r.area}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Map + Address */}
      <section className="py-20 bg-[#faf7f1]">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-10 items-stretch">
          <div className="rounded-3xl overflow-hidden shadow-lg h-[400px]">
            <iframe
              title={`Map — ${loc.area}`}
              src={`https://www.google.com/maps?q=${encodeURIComponent(loc.address.mapQuery)}&output=embed`}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          <div className="bg-white rounded-3xl p-8 md:p-10 flex flex-col">
            <div className="text-sm tracking-[0.2em] uppercase text-[#c9743a] mb-3">Visit or connect</div>
            <h3 className="font-serif text-3xl text-[#1a2b17]">NutrifyMe · {loc.area}</h3>
            <div className="mt-6 space-y-4 text-[#2a3a24]">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#3a5a2c] flex-shrink-0 mt-0.5" />
                <div>
                  {loc.address.line1},<br />
                  {loc.address.line2},<br />
                  {loc.address.area}, {loc.address.cityLine}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#3a5a2c]" />
                <a href="tel:+919545405936" className="hover:text-[#c9743a]">+91 95454 05936</a>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-[#3a5a2c]" />
                <span>Mon – Sat · 9:00 am to 7:00 pm</span>
              </div>
            </div>
            <div className="mt-auto pt-8 flex flex-wrap gap-3">
              <Button onClick={() => setBookOpen(true)} className="h-12 px-6 rounded-full bg-[#3a5a2c] hover:bg-[#2d4622] text-white">Book Free Call</Button>
              <a href="https://wa.me/919545405936" target="_blank" rel="noopener noreferrer" className="h-12 px-6 rounded-full border border-[#1a2b17]/20 text-[#1a2b17] hover:bg-[#1a2b17] hover:text-white inline-flex items-center transition">WhatsApp</a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-sm tracking-[0.2em] uppercase text-[#c9743a] mb-3">FAQs</div>
          <h2 className="font-serif text-4xl text-[#1a2b17] mb-10">Common questions from {loc.area} families</h2>
          <div className="space-y-5">
            {loc.faq.map((f, i) => (
              <div key={i} className="p-6 rounded-3xl bg-[#faf7f1]">
                <div className="font-medium text-[#1a2b17]">{f.q}</div>
                <div className="mt-2 text-[#4a5544] leading-relaxed">{f.a}</div>
              </div>
            ))}
          </div>

          <div className="mt-14 text-sm text-[#7a8471]">
            Related reading: <Link to="/blog/7-day-pcos-diet-plan-indian" className="text-[#c9743a] hover:underline">7-Day PCOS Diet Plan</Link> · <Link to="/blog/diabetic-friendly-indian-recipes" className="text-[#c9743a] hover:underline">Diabetic-friendly recipes</Link> · <Link to="/blog/weight-loss-mistakes-indian-women" className="text-[#c9743a] hover:underline">Weight loss mistakes</Link>
          </div>
        </div>
      </section>

      <Footer onBook={() => setBookOpen(true)} />
      <BookingModal open={bookOpen} onClose={() => setBookOpen(false)} />
    </div>
  );
};

export default LocationLanding;
